import { MNSproCloudApi } from '../..';
import {
  AzureGroupAssignmentResponse,
  AzureGroupAssignmentUpdateRequestItem,
  AzureGroupInfoResponse,
  LessonNameAndType,
  OrganizationResponse,
  OrganizationTimeTableOptionsUpdateRequest,
} from '../../api/v2';

/**
 * Local, id-based model carried from the timetable options UI to the saga
 * (see web-app timetable-options feature) when persisting course-to-Azure-group
 * assignments. Names are only used for display; the id is authoritative.
 */
export interface CourseAssignmentUpdate {
  entityRevisionsId: string;
  entityType: LessonNameAndType['groupType'];
  azureGroupId: string | null;
}

const createClassRegisterApi = (authToken: string) =>
  MNSproCloudApi.V2.ClassRegisterApiFactory(
    MNSproCloudApi.V2.createConfiguration(authToken),
  );

const createOrganizationApi = (authToken: string) =>
  MNSproCloudApi.V2.OrganizationsApiFactory(
    MNSproCloudApi.V2.createConfiguration(authToken),
  );

export class TimeTableOptionsService {
  /**
   * Fetches the current timetable options for the specified organization.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to fetch options for.
   * @returns The organization's current timetable options.
   */
  static async getTimeTableOptions(
    authToken: string,
    organizationId: string,
  ): Promise<OrganizationResponse> {
    const api = createOrganizationApi(authToken);
    return (await api.get(organizationId)).data;
  }

  /**
   * Fetches the current id-based course-to-Azure-group assignments for the
   * specified organization.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to fetch assignments for.
   * @returns The current Azure group assignments, keyed by entity revision id.
   */
  static async getCourseMappings(
    authToken: string,
    organizationId: string,
  ): Promise<AzureGroupAssignmentResponse[]> {
    const api = createClassRegisterApi(authToken);
    return (await api.getTimetableMappings(organizationId)).data;
  }

  /**
   * Fetches the timetable-import lesson names available for mapping,
   * including their entity revision id and group type.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to fetch lesson names for.
   * @returns The available timetable lesson names.
   */
  static async getTimetableCourses(
    authToken: string,
    organizationId: string,
  ): Promise<LessonNameAndType[]> {
    const api = createClassRegisterApi(authToken);
    return (await api.getTimetableLessonNames(organizationId)).data;
  }

  /**
   * Fetches the MNSpro Cloud (Azure) groups that timetable lessons can be
   * mapped to.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to fetch groups for.
   * @returns The available Azure groups.
   */
  static async getMNSproCourses(
    authToken: string,
    organizationId: string,
  ): Promise<AzureGroupInfoResponse[]> {
    const api = createClassRegisterApi(authToken);
    return (await api.getTimetableCourseNames(organizationId)).data;
  }

  /**
   * Persists id-based course-to-Azure-group assignments and returns the
   * refreshed list of assignments.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to update assignments for.
   * @param assignments - Assignments to persist, referencing entities by
   * entity revision id.
   * @returns The Azure group assignments after the update was applied.
   */
  static async updateCourseMappings(
    authToken: string,
    organizationId: string,
    assignments: CourseAssignmentUpdate[],
  ): Promise<AzureGroupAssignmentResponse[]> {
    const api = createClassRegisterApi(authToken);
    await api.updateTimetableMappings(
      organizationId,
      assignments.map<AzureGroupAssignmentUpdateRequestItem>(a => ({
        entityRevisionsId: a.entityRevisionsId,
        entityType: a.entityType,
        azureGroupId: a.azureGroupId,
      })),
    );
    return await TimeTableOptionsService.getCourseMappings(
      authToken,
      organizationId,
    );
  }

  /**
   * Updates the timetable options for the specified organization.
   *
   * @param authToken - Bearer token used to authenticate the request.
   * @param organizationId - Id of the organization to update.
   * @param apiRequest - The timetable options update payload.
   * @returns The organization's timetable options after the update was applied.
   */
  static async updateTimeTableOptions(
    authToken: string,
    organizationId: string,
    apiRequest: OrganizationTimeTableOptionsUpdateRequest,
  ): Promise<OrganizationResponse> {
    const api = createOrganizationApi(authToken);
    return (await api.updateTimeTableOptions(organizationId, apiRequest)).data;
  }
}
